"use client"

// React imports
import { useEffect, useRef } from "react"

// Third-party library imports
import { openModal } from "@locmod/modal"
import localStorage from "@locmod/local-storage"
import { useBonuses } from "@azuro-org/sdk"
import type { Freebet } from "@azuro-org/toolkit"
import { BonusStatus } from "@azuro-org/toolkit"
import { type Address } from "viem"
import { useChainId, useChains } from "wagmi"

// Local utility and hook imports
import { useWalletAuth } from "@/hooks/useWalletAuth"
import { useUserContext } from "@/contexts/user"
import { AFFILIATE_ADDRESS } from "@/config/env"
import useChainProviderHook from "@/hooks/useChainProvider"

const NewFreeBetsChecker: React.FC = () => {
  const { account } = useWalletAuth()
  const { isUserConnecting, isLoginComplete } = useUserContext()
  const chainId = useChainId()
  const chains = useChains()
  const { isOvertimeChain } = useChainProviderHook()
  const chainName = chains.find(chain => chain.id === chainId)?.name

  const { data: bonuses, refetch: refetchBonuses } = useBonuses({
    account,
    affiliate: AFFILIATE_ADDRESS as Address,
    bonusStatus: BonusStatus.Available,
    query: {
      enabled: Boolean(account) && !isUserConnecting && isLoginComplete,
    },
  })

  // Track if we've already processed modals for this session
  const hasProcessedModals = useRef(false)
  const promotionModalShown = useRef(false)
  const lastProcessedChainId = useRef<number | null>(null)
  const currentChainId = useRef<number | null>(null)
  const userSwitchedChain = useRef(false)
  const hasRefetchedBonuses = useRef(false)

  useEffect(() => {
    // Reset modal processing when chain changes
    if (lastProcessedChainId.current !== chainId) {
      hasProcessedModals.current = false
      promotionModalShown.current = false
      lastProcessedChainId.current = chainId
      currentChainId.current = chainId
      userSwitchedChain.current = false
    }
  }, [chainId])

  useEffect(() => {
    if (!isLoginComplete || isUserConnecting || hasProcessedModals.current) {
      return
    }

    // Ensure we're processing for the current chain
    if (currentChainId.current !== chainId) {
      return
    }

    const processModals = async () => {
      hasProcessedModals.current = true

      // Check if promotion modal should be shown
      const shouldShowPromotionModal = !isOvertimeChain
      // const promotionStorageName = constants.localStorageKeys.promotionalModal
      // const promotionWasShown = localStorage.getItem(promotionStorageName)
      // // const isSiweLogin =
      // //   localStorage.getItem(constants.localStorageKeys.loginMethod) === 'siwe'

      // if (shouldShowPromotionModal && !promotionWasShown) {
      //   // Show promotion modal first
      //   promotionModalShown.current = true
      //   await new Promise<void>(resolve => {
      //     openModal("PromotionalModal", {
      //       chainName: chainName || "Polygon",
      //       onClose: () => {
      //         localStorage.setItem(promotionStorageName, "true")
      //         resolve()
      //       },
      //       onChainSwitch: () => {
      //         userSwitchedChain.current = true
      //       },
      //     })
      //   })
      // }

      // Skip free bet modals if user switched chains in promotion modal
      if (userSwitchedChain.current) {
        return
      }

      // Try to get bonuses data, with retry mechanism for initial load
      let bonusesData = bonuses
      if (!bonusesData && !hasRefetchedBonuses.current) {
        hasRefetchedBonuses.current = true
        // Wait a bit and try to refetch bonuses
        await new Promise(resolve => setTimeout(resolve, 1000))
        const refetchResult = await refetchBonuses()
        bonusesData = refetchResult.data
      }

      // Only show free bets if we're still on the same chain and have data
      if (bonusesData?.length && currentChainId.current === chainId) {
        const now = Date.now()
        const activeFreebets = bonusesData.filter((freebet: Freebet) => {
          return (
            freebet.expiresAt && new Date(freebet.expiresAt).getTime() >= now
          )
        })

        // Filter freebets to only show those for the current chain
        const chainSpecificFreebets = activeFreebets.filter(
          (freebet: Freebet) => {
            const freebetChainId = (freebet as any).chainId
            return freebetChainId === chainId
          }
        )

        // Add a 2-second delay before showing free bet modals
        await new Promise(resolve => setTimeout(resolve, 2000))

        // Show free bet modals one by one
        for (const freebet of chainSpecificFreebets) {
          const uniqueId = freebet.id
          const storageName = `bonus-${uniqueId}`
          const wasShown = localStorage.getItem<boolean>(storageName)

          if (!wasShown) {
            await new Promise<void>(resolve => {
              openModal("NewFreeBetModal", {
                freebet,
                onClose: () => {
                  localStorage.setItem(storageName, true)
                  resolve()
                },
              })
            })
          }
        }
      }
    }

    processModals()
  }, [
    isLoginComplete,
    isUserConnecting,
    isOvertimeChain,
    chainName,
    bonuses,
    chainId,
    refetchBonuses,
  ])

  return null
}

export default NewFreeBetsChecker
